import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Stack,
  Button,
  Grid,
  Container,
} from "@mui/material";

//... Icons imports
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import HomeIcon from "@mui/icons-material/Home";

//... Shared component imports.
import EmptyUI from "../../sharedComponents/emptyStateUIContainers/EmptyUI";

const useStyles = makeStyles((theme) => ({
  drpPlayer: {
    userSelect: "none",
    padding: 12,
    margin: "0 0 4px 0",
    borderRadius: theme.spacing(0.5),
    fontWeight: "400",
    minHeight: "20px",
    backgroundColor: "#fff",
    color: "#1d428a",
    borderColor: "#bbb",
    borderWidth: "1px",
    borderStyle: "solid",
    textAlign: "center",
  },
  drpPlayerIsDragging: {
    backgroundColor: "#263B4A !important",
    color: "#fff !important",
  },
  drpPlayerModified: {
    color: "#C8102E",
    fontWeight: "500",
    borderColor: "#f00 !important",
    borderWidth: "1px !important",
    borderStyle: "dashed !important",
    marginBottom: "6px !important",
  },
  drgContainer: {
    background: "#e9e9e9",
    padding: 4,
    width: 250,
    height: "calc(100vh - 290px)",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#bdbdbd",
    },
    "&::-webkit-scrollbar-thumb": {
      boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.7)",
    },
  },
  drgContainerIsDragging: {
    background: "#e5edfb !important",
  },
  dragDropTeamHeader: {
    height: "77px",
    "& > span": {
      color: "#1d428a",
      width: "80%",
      padding: "8px",
      textAlign: "center",
      fontWeight: "500 !important",
      fontSize: "1.2rem !important",
    },
  },
  stickyColumn: {
    position: "sticky",
    background: "white",
    left: 0,
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      position: "static",
      left: "auto",
      zIndex: "auto",
    },
  },
  dragDropContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    overflowX: "auto",
  },
  dragDropItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
}));

const ManagePlayersAssignments = ({
  teamList,
  playersList,
  setPlayersList,
  updateEventGameTeamPlayer,
}) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState("");
  const [refListModified, setRefListModified] = useState(false);

  useEffect(() => {
    document.body.style.backgroundColor = "#ffff";

    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    IsRefListModified();
  }, [playersList]);

  const IsRefListModified = () => {
    setRefListModified(
      playersList.filter((r) => r.teamId !== r.updatedTeamId).length > 0
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.droppableId === result.source.droppableId) return;

    try {
      let __cureentTempPlayerList = [...playersList];
      let __resultRefID = result.draggableId;
      let __resultDroppableID = result.destination.droppableId;
      let refIndex = __cureentTempPlayerList.findIndex(
        (r) => r.playerId + "" === __resultRefID
      );
      if (refIndex > -1) {
        __cureentTempPlayerList[refIndex].updatedTeamId =
          parseInt(__resultDroppableID);
        setPlayersList([...__cureentTempPlayerList]);
      }
    } catch (e) {
      console.error("onDragEnd", e);
      return;
    } finally {
      IsRefListModified();
    }
  };

  // Handle search input
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter items based on search query
  const filteredItems = playersList?.filter((item) => {
    const firstNameMatch = searchQuery
      ? item?.playerName
          ?.toLowerCase()
          ?.includes(searchQuery.trim().toLowerCase())
      : true;

    return (
      item?.teamId > 0 ||
      item?.updatedTeamId > 0 ||
      (item?.teamId === 0 && item?.updatedTeamId === 0 && firstNameMatch)
    );
  });

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="row" alignItems="center">
        {teamList && teamList.length > 0 && (
          <Button
            onClick={updateEventGameTeamPlayer}
            variant="contained"
            color="primary"
            disabled={!refListModified}
            style={{ margin: "10px 0 5px 10px" }}
          >
            Save
          </Button>
        )}
      </Box>
      <Box className={classes.dragDropContainer}>
        <DragDropContext onDragEnd={onDragEnd}>
          {teamList.length === 0 ? (
            <>
              <Grid container p={1}>
                <Grid item lg={12}>
                  <EmptyUI
                    heading="No results were found based on your search criteria."
                    subHeading={`Please select all filters and click "Search" button.`}
                    textColor="#000000"
                    icon={<ContactSupportIcon />}
                    iconStyles={{
                      fontSize: "clamp(10rem, 1vw + 8rem, 10rem)",
                      color: "#8c9dc4",
                    }}
                    styles={{
                      minHeight: "70vh",
                    }}
                    actionLabel="Home"
                    actionIcon={<HomeIcon />}
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            teamList.map((da, index) => {
              return (
                <Box
                  key={`drg_${da.eventTeamId}`}
                  className={
                    index === 0
                      ? `${classes.dragDropItem} ${classes.stickyColumn}`
                      : classes.dragDropItem
                  }
                >
                  <div className={classes.dragDropTeamHeader}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection:
                            index === 0 ? "column" : "column-reverse",
                        }}
                      >
                        <Typography
                          variant={index === 0 ? "subtitle2" : "h6"}
                          sx={{
                            flexGrow: 1,
                            marginTop: index === 0 ? 0 : "9px",
                          }}
                        >
                          {da.teamName}
                        </Typography>

                        {index === 0 && (
                          <TextField
                            label="Search"
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={handleSearch}
                            sx={{ mb: 2, width: "250px" }}
                          />
                        )}

                        {index !== 0 && (
                          <Box sx={{ visibility: "hidden" }}>Hidden rows</Box>
                        )}
                      </Box>
                    </Stack>
                  </div>

                  <Paper style={{ margin: 8 }} elevation={2}>
                    <Droppable
                      droppableId={da.eventTeamId + ""}
                      key={`drp_${da.eventTeamId}`}
                      index={index}
                    >
                      {(provided, snapshot) => {
                        return (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className={`${classes.drgContainer} ${
                              snapshot.isDraggingOver
                                ? classes.drgContainerIsDragging
                                : ""
                            }`}
                          >
                            {filteredItems
                              .filter((r) => r.updatedTeamId === da.eventTeamId)
                              .map((item, index) => {
                                return (
                                  <Draggable
                                    key={`drg_${item.playerId}`}
                                    draggableId={item.playerId + ""}
                                    index={index}
                                  >
                                    {(provided, snapshot) => {
                                      return (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          style={{
                                            ...provided.draggableProps.style,
                                          }}
                                          className={`${classes.drpPlayer} ${
                                            snapshot.isDragging
                                              ? classes.drpPlayerIsDragging
                                              : ""
                                          } ${
                                            item.teamId !== item.updatedTeamId
                                              ? classes.drpPlayerModified
                                              : ""
                                          }`}
                                        >
                                          {`${item.playerName}`}
                                        </div>
                                      );
                                    }}
                                  </Draggable>
                                );
                              })}
                            {provided.placeholder}
                          </div>
                        );
                      }}
                    </Droppable>
                  </Paper>
                </Box>
              );
            })
          )}
        </DragDropContext>
      </Box>
    </Container>
  );
};

export default ManagePlayersAssignments;
